/**
 * PublishAppModal Component
 *
 * Modal for publishing an application to the marketplace.
 * Allows selecting from deployments with valid versionTag attributes.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import type { MarketplaceListingCreateDTO, MarketplaceCategory } from '../../../types/marketplace.types';
import { MARKETPLACE_CATEGORIES } from '../../../types/marketplace.types';
import type { ApplicationSanitized } from '../../../types/application.types';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { deploymentService } from '../../../api/deployment.service';
import { usePublishToMarketplace } from '../hooks/usePublishedListings';
import { getErrorMessage } from '../../../lib/utils/errors';
import { formatDistanceToNow } from 'date-fns';

interface PublishAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  application: ApplicationSanitized;
  tenantId: string;
}

/**
 * PublishAppModal component
 */
export const PublishAppModal: React.FC<PublishAppModalProps> = ({
  isOpen,
  onClose,
  application,
  tenantId,
}) => {
  const [name, setName] = useState(application.applicationName);
  const [description, setDescription] = useState(application.description || '');
  const [category, setCategory] = useState<MarketplaceCategory>('other');
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string>('');
  const [releaseNotes, setReleaseNotes] = useState('Initial release');
  const [documentationUrl, setDocumentationUrl] = useState('');
  const [repositoryUrl, setRepositoryUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const publishMutation = usePublishToMarketplace();

  // Fetch deployments for this application
  const {
    data: deployments = [],
    isLoading: deploymentsLoading,
    error: deploymentsError,
  } = useQuery({
    queryKey: ['deployments', tenantId, application.applicationId],
    queryFn: () => deploymentService.listDeployments(tenantId, application.applicationId),
    enabled: isOpen,
  });

  // Filter deployments that have a versionTag
  const taggedDeployments = useMemo(() => {
    return deployments.filter((d: DeploymentSanitized) => d.versionTag && d.deploymentStatus === 'deployed');
  }, [deployments]);

  // Get selected deployment details
  const selectedDeployment = useMemo(() => {
    return taggedDeployments.find((d: DeploymentSanitized) => d.deploymentId === selectedDeploymentId);
  }, [taggedDeployments, selectedDeploymentId]);

  const handlePublish = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }
    // Backend requires minimum 20 characters for description
    if (description.trim().length < 20) {
      setError('Description must be at least 20 characters');
      return;
    }
    if (description.trim().length > 2000) {
      setError('Description must not exceed 2000 characters');
      return;
    }
    if (!selectedDeploymentId) {
      setError('Please select a deployment to publish');
      return;
    }

    const data: MarketplaceListingCreateDTO = {
      sourceApplicationId: application.applicationId,
      deploymentId: selectedDeploymentId,
      name: name.trim(),
      description: description.trim(),
      category,
      releaseNotes: releaseNotes.trim() || undefined,
      documentationUrl: documentationUrl.trim() || undefined,
      repositoryUrl: repositoryUrl.trim() || undefined,
    };

    try {
      await publishMutation.mutateAsync({ tenantId, data });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <div>
          <p className="text-lg font-semibold text-gray-900">Publish to Marketplace</p>
          <p className="mt-1 text-sm text-gray-500">
            Publishing: <span className="font-medium text-gray-900">{application.applicationName}</span>
            {' · '}Application Code: {application.applicationCode}
          </p>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Deployment Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Deployment *
            </label>
            {deploymentsLoading ? (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                Loading deployments...
              </div>
            ) : deploymentsError ? (
              <Alert variant="error" className="text-sm">
                Failed to load deployments: {getErrorMessage(deploymentsError)}
              </Alert>
            ) : taggedDeployments.length === 0 ? (
              <Alert variant="warning" className="text-sm">
                <p className="font-medium">No publishable deployments found</p>
                <p className="mt-1">
                  Only deployments with a version tag can be published to the marketplace.
                  Please set a version tag on a deployed version first.
                </p>
              </Alert>
            ) : (
              <>
                <select
                  value={selectedDeploymentId}
                  onChange={(e) => setSelectedDeploymentId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a deployment...</option>
                  {taggedDeployments.map((deployment: DeploymentSanitized) => (
                    <option key={deployment.deploymentId} value={deployment.deploymentId}>
                      {deployment.versionTag} - v{deployment.version}
                      {deployment.isLatest ? ' (LATEST)' : ''}
                      {deployment.deploymentId === application.currentDeploymentId ? ' (ACTIVE)' : ''}
                    </option>
                  ))}
                </select>
                {selectedDeployment && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-md text-sm">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                        {selectedDeployment.versionTag}
                      </span>
                      <span className="text-gray-600">
                        Deployed {formatDistanceToNow(new Date(selectedDeployment.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      This version will be the initial release in the marketplace.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Listing Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Application"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what your application does (minimum 20 characters)..."
              rows={4}
              maxLength={2000}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 ${
                description.trim().length > 0 && description.trim().length < 20
                  ? 'border-amber-300'
                  : 'border-gray-300'
              }`}
            />
            <p className={`mt-1 text-xs ${
              description.trim().length > 0 && description.trim().length < 20
                ? 'text-amber-600'
                : 'text-gray-500'
            }`}>
              {description.trim().length}/2000 characters
              {description.trim().length > 0 && description.trim().length < 20 && (
                <span className="ml-2">(minimum 20 required)</span>
              )}
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as MarketplaceCategory)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              {MARKETPLACE_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Release Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Release Notes
            </label>
            <Input
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder="Initial release"
            />
          </div>

          {/* URLs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Documentation URL
              </label>
              <Input
                value={documentationUrl}
                onChange={(e) => setDocumentationUrl(e.target.value)}
                placeholder="https://docs.example.com"
                type="url"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Repository URL
              </label>
              <Input
                value={repositoryUrl}
                onChange={(e) => setRepositoryUrl(e.target.value)}
                placeholder="https://github.com/..."
                type="url"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <Alert variant="error">{error}</Alert>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handlePublish}
          disabled={publishMutation.isPending || taggedDeployments.length === 0 || !selectedDeploymentId}
        >
          {publishMutation.isPending ? 'Publishing...' : 'Publish to Marketplace'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PublishAppModal;
